import React from 'react';
import { useWordPressContext } from 'contexts/wordpress-provider';
import Image from 'next/image';

import { LocationHoursWeek, LocationItem } from 'lib/queries/locations';

import { Card, Paragraph } from '@leafwell/components';
import LocationOpeningHours from './location-opening-hours';
import HeadingBody from './heading-body';
import LinkArrow from './link-arrow';
import Link from 'next/link';

export type LocationCardProps = {
  onChange?: (id: LocationItem['id']) => void;
  category?: string;
  features?: string[];
  formattedAddress?: string;
  id?: number | string;
  name?: string;
  openingHours?: LocationHoursWeek;
  rating?: number;
  state?: string;
  uri?: string;
  isActive?: boolean;
  layout?: 'horizontal' | 'vertical';
};

const LocationCard: React.FC<LocationCardProps> = ({
  category,
  features,
  formattedAddress,
  id,
  name,
  openingHours,
  rating,
  state,
  uri,
  isActive,
  layout = 'horizontal',
  onChange = () => null,
}) => {
  const { translations } = useWordPressContext();
  const isHorizontal = layout === 'horizontal';
  const hasCategory = category?.length > 0;
  const hasUri = uri?.length > 0;

  return (
    <Card
      {...{
        className: [
          'shrink-0 grid-cols-inline-form items-center gap-2',
          isHorizontal
            ? 'w-80 md:w-full lg:w-112 shadow-md-radius my-5 md:mb-0 shadow-lg border-inherit duration-300 transition-transform mx-2 md:mx-3'
            : 'w-64 md:w-70 lg:w-72 xl:w-76',
          '!border-gray-200',
          isActive
            ? 'md:-translate-y-5 motion-reduce:transition-none motion-reduce:!transform-none'
            : '',
          hasCategory ? '' : '!items-start gap-y-6 md:gap-y-9',
        ].join(' '),
        filled: isHorizontal ? true : false,
        id: id.toString(),
        onClick: () => onChange(id),
        rounded: true,
        variant: isHorizontal ? 'white' : 'default',
      }}
    >
      {category && <Paragraph {...{ size: 'small' }}>{category}</Paragraph>}
      <span>
        {openingHours && (
          <LocationOpeningHours
            {...{
              className: 'justify-self-end col-start-2',
              openingHours,
              showHours: false,
            }}
          />
        )}
        {!category && rating > 0 && (
          <Paragraph
            {...{
              className:
                'font-medium flex gap-1 justify-self-end items-center col-start-2 mt-2 pl-1',
            }}
          >
            {rating}
            <Image src="/star.svg" width={14} height={13} alt="" />
          </Paragraph>
        )}
      </span>

      <header className={hasCategory ? '' : 'row-start-1'}>
        <Link href={hasUri ? uri : '#'}>
          <HeadingBody
            {...{
              className: [
                '!leading-none pb-2',
                hasUri
                  ? 'hover:cursor-pointer hover:text-green'
                  : 'pointer-events-none',
                isHorizontal ? '!text-lg md:!text-3xl' : '!text-lg',
              ].join(' '),
              level: isHorizontal ? '4' : '5',
              title: name,
            }}
          />
        </Link>
        <Paragraph {...{ variant: 'medium' }}>{translations[state]}</Paragraph>
      </header>
      {category && (
        <Paragraph
          {...{
            className: 'font-medium flex gap-1 justify-self-end items-center',
          }}
        >
          {rating}
          <Image src="/star.svg" width={14} height={13} alt="" />
        </Paragraph>
      )}
      {features?.length > 0 && (
        <section {...{ className: 'col-span-full' }}>
          <Paragraph>{translations['Features']}</Paragraph>
          {features.map((feature, featureKey) => (
            <Paragraph
              {...{
                key: featureKey,
                tagName: 'span',
                variant: 'medium',
              }}
            >
              {feature}
              {featureKey < features.length - 1 && <span>, </span>}
            </Paragraph>
          ))}
        </section>
      )}
      <section className={isHorizontal ? '' : 'pt-22 xl:pt-28'}>
        <Paragraph>{translations['Address']}</Paragraph>
        <Paragraph className="text-sm lg:text-base" {...{ variant: 'medium' }}>
          {formattedAddress}
        </Paragraph>
      </section>
      {uri && (
        <LinkArrow
          {...{
            className: [
              'w-10 h-10 justify-self-end justify-end',
              isHorizontal ? '' : 'pt-28',
            ].join(' '),
            title: translations['Learn more'],
            showLine: false,
            showTitle: false,
            uri,
          }}
        />
      )}
    </Card>
  );
};

export default LocationCard;
